<script setup lang="ts">
/**
 * Drapeau d'une langue du site, en SVG intégré : les emojis drapeaux ne
 * s'affichent pas sous Windows (lettres « FR » à la place).
 * Anglais = Royaume-Uni (locale en-GB). Espagne sans les armoiries : illisibles
 * à cette taille.
 */
import type { Langue } from "~/utils/langue";

defineProps<{ code: Langue }>();

// Identifiants uniques : le drapeau britannique utilise des clipPath, et
// plusieurs drapeaux identiques coexistent dans la page.
const id = useId();
</script>

<template>
  <span
    class="inline-flex h-3.5 w-5 shrink-0 overflow-hidden rounded-[3px] ring-1 ring-black/10"
    aria-hidden="true"
  >
    <svg
      v-if="code === 'fr'"
      viewBox="0 0 3 2"
      preserveAspectRatio="xMidYMid slice"
      class="size-full"
    >
      <rect width="1" height="2" fill="#002654" />
      <rect x="1" width="1" height="2" fill="#fff" />
      <rect x="2" width="1" height="2" fill="#CE1126" />
    </svg>

    <svg
      v-else-if="code === 'en'"
      viewBox="0 0 60 30"
      preserveAspectRatio="xMidYMid slice"
      class="size-full"
    >
      <clipPath :id="`${id}-s`"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
      <clipPath :id="`${id}-t`"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
      <g :clip-path="`url(#${id}-s)`">
        <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6" />
        <path
          d="M0,0 L60,30 M60,0 L0,30"
          :clip-path="`url(#${id}-t)`"
          stroke="#C8102E"
          stroke-width="4"
        />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6" />
      </g>
    </svg>

    <svg
      v-else-if="code === 'de'"
      viewBox="0 0 5 3"
      preserveAspectRatio="xMidYMid slice"
      class="size-full"
    >
      <rect width="5" height="1" fill="#000" />
      <rect y="1" width="5" height="1" fill="#DD0000" />
      <rect y="2" width="5" height="1" fill="#FFCE00" />
    </svg>

    <svg
      v-else-if="code === 'es'"
      viewBox="0 0 3 2"
      preserveAspectRatio="xMidYMid slice"
      class="size-full"
    >
      <rect width="3" height="2" fill="#AA151B" />
      <rect y="0.5" width="3" height="1" fill="#F1BF00" />
    </svg>

    <svg
      v-else-if="code === 'it'"
      viewBox="0 0 3 2"
      preserveAspectRatio="xMidYMid slice"
      class="size-full"
    >
      <rect width="1" height="2" fill="#009246" />
      <rect x="1" width="1" height="2" fill="#fff" />
      <rect x="2" width="1" height="2" fill="#CE2B37" />
    </svg>

    <svg
      v-else-if="code === 'nl'"
      viewBox="0 0 9 6"
      preserveAspectRatio="xMidYMid slice"
      class="size-full"
    >
      <rect width="9" height="2" fill="#AE1C28" />
      <rect y="2" width="9" height="2" fill="#fff" />
      <rect y="4" width="9" height="2" fill="#21468B" />
    </svg>
  </span>
</template>
